import type React from "react";
import { useState } from "react";
import type { z } from "zod";
import { Spinner } from "@/components/Spinner";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup } from "@/components/ui/radio-group";
import { useUserData } from "@/components/user/lib/hooks/useUserData";
import { cn } from "@/lib/utils";
import { CheckboxOption } from "@/shared/components/CheckboxOption";
import { ErrorMessage } from "@/shared/components/ErrorMessage";
import { RadioOption } from "@/shared/components/RadioOption";
import {
	formClassNames,
	formHeaderClassNames,
} from "@/shared/constants/customStyleSets";
import { initialFormData, surveySchema } from "../const";
import { handleCheckBoxChange } from "../helpers/handleCheckBoxChange";
import { handleRadioChange } from "../helpers/handleRadioChange";
import type { SurveyData } from "../types";
import { QuestionTitle } from "./components/QuestionTitle";
import "./UserSurvey.css";

const mapSurveyData = (data: SurveyData) => {
	const riskToleranceMap: Record<string, number> = {
		low: 1,
		medium: 2,
		high: 3,
		veryHigh: 4,
	};

	const investmentHorizonMap: Record<string, number> = {
		lessThan12Months: 1,
		"1-3Years": 2,
		"3-5Years": 3,
		"5+Years": 4,
	};

	const portfolioSizeMap: Record<string, number> = {
		less50k: 1,
		"50-100k": 2,
		more100k: 3,
		over1m: 4,
	};

	const investmentDurationMap: Record<string, number> = {
		less12months: 1,
		"1-5Years": 2,
		"5-10Years": 3,
		over10Years: 4,
	};

	const expertiseMap: Record<string, number> = {
		start: 1,
		basic: 2,
		intermediate: 3,
		expert: 4,
	};

	return {
		investingCharacteristics: {
			riskTolerance: riskToleranceMap[data.riskTolerance],
			investmentHorizon: investmentHorizonMap[data.investmentHorizon],
			portfolioSize: portfolioSizeMap[data.portfolioSize],
			investmentDuration: investmentDurationMap[data.investmentDuration],
			expertise: expertiseMap[data.expertise],
			investingStyle: {
				growth: data.stockTypes.includes("growthStocks"),
				income: data.stockTypes.includes("income"),
				value: data.stockTypes.includes("value"),
			},
			options: data.otherInvestments.includes("options") ? 1 : 0,
			etfFunds: data.otherInvestments.includes("etf") ? 1 : 0,
			futures: data.otherInvestments.includes("futures") ? 1 : 0,
			mutualFunds: data.otherInvestments.includes("funds") ? 1 : 0,
			investorType: {
				tradeStocks: data.investorType.includes("tradeStocks"),
				generallyInvest: data.investorType.includes("generallyInvest"),
				investor: data.investorType.includes("investor"),
				options: data.investorType.includes("options"),
			},
			tools: {
				finvest: data.tools.includes("finvest"),
				bankBroker: data.tools.includes("bankBroker"),
				fundManager: data.tools.includes("fundManager"),
				ibd: data.tools.includes("ibd"),
				benzinga: data.tools.includes("benzinga"),
				vectorVest: data.tools.includes("vectorVest"),
				tradingView: data.tools.includes("tradingView"),
				stockTwits: data.tools.includes("stockTwits"),
				eTorro: data.tools.includes("eTorro"),
				web: data.tools.includes("web"),
				other: data.tools.includes("other"),
			},
		},
		goal: data.goal.join(", "),
		termsConfirmed: data.terms === "on",
	};
};

type Props = {
	onSuccess?: () => void;
	onError?: () => void;
};

export const UserSurvey: React.FC<Props> = ({ onError, onSuccess }) => {
	const [validationErrors, setValidationErrors] = useState<z.ZodError | null>(
		null,
	);
	const [updateError, setUpdateError] = useState<string | null>(null);
	const [form, setForm] = useState<SurveyData>(initialFormData);
	const { isLoadingUpdate, updateUserData } = useUserData();

	async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setValidationErrors(null);
		setUpdateError(null);

		const result = surveySchema.safeParse(form);

		if (!result.success) {
			console.error("Validation error:", result.error);
			setValidationErrors(result.error);
			return;
		}

		try {
			await updateUserData(mapSurveyData(result.data));

			onSuccess?.();
		} catch (_err) {
			setUpdateError(
				"We couldn't update your data. Please try again or contact us at support@epsmomentum.com for assistance.",
			);
			onError?.();
		}
	}

	return (
		<form
			className={cn(
				formClassNames,
				"max-w-[800px] max-h-[90%] overflow-y-auto",
			)}
			onSubmit={handleSubmit}
		>
			<div className="survey-container ">
				<h1 className={formHeaderClassNames}>About Me, My Profile</h1>

				<div className="questions flex gap-4 flex-wrap align-center justify-center">
					<div className="left-side w-full max-w-[600px]">
						{/* Question 1 riskTolerance */}
						<div className="survey-question">
							<QuestionTitle title="What risk scale are you comfortable with?" />
							<RadioGroup className="flex flex-col mb-0" name="riskTolerance">
								<div className="optionLine">
									<RadioOption
										name="riskTolerance"
										value="low"
										id="low"
										label="Low"
										handleChange={() =>
											handleRadioChange("low", "riskTolerance", setForm)
										}
									/>
									<RadioOption
										name="riskTolerance"
										value="medium"
										id="medium"
										label="Medium"
										handleChange={() =>
											handleRadioChange("medium", "riskTolerance", setForm)
										}
									/>
								</div>

								<div className="optionLine">
									<RadioOption
										name="riskTolerance"
										value="high"
										id="high"
										label="High"
										handleChange={() =>
											handleRadioChange("high", "riskTolerance", setForm)
										}
									/>
									<RadioOption
										name="riskTolerance"
										value="veryHigh"
										id="veryHigh"
										label="Very High"
										handleChange={() =>
											handleRadioChange("veryHigh", "riskTolerance", setForm)
										}
									/>
								</div>
							</RadioGroup>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "riskTolerance",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 2 investmentHorizon*/}
						<div className="survey-question">
							<QuestionTitle title="What is your investment horizon?" />

							<RadioGroup
								className="flex flex-col mb-0"
								name="investmentHorizon"
							>
								<div className="optionLine">
									<RadioOption
										name="investmentHorizon"
										value="lessThan12Months"
										id="lessThan12Months"
										label="Less than 12 months"
										handleChange={() =>
											handleRadioChange(
												"lessThan12Months",
												"investmentHorizon",
												setForm,
											)
										}
									/>

									<RadioOption
										name="investmentHorizon"
										value="1-3Years"
										id="1-3Years"
										label="1-3 years"
										handleChange={() =>
											handleRadioChange(
												"1-3Years",
												"investmentHorizon",
												setForm,
											)
										}
									/>
								</div>

								<div className="optionLine">
									<RadioOption
										name="investmentHorizon"
										value="3-5Years"
										id="3-5Years"
										label="3-5Years"
										handleChange={() =>
											handleRadioChange(
												"3-5Years",
												"investmentHorizon",
												setForm,
											)
										}
									/>

									<RadioOption
										name="investmentHorizon"
										value="5+Years"
										id="5+Years"
										label="5+Years"
										handleChange={() =>
											handleRadioChange("5+Years", "investmentHorizon", setForm)
										}
									/>
								</div>
							</RadioGroup>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "investmentHorizon",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 3 expertise */}
						<div className="survey-question">
							<div className="text-lg font-bold mb-2 flex flex-col">
								<p>
									Where on the Stock Investing expertise scale do you put
									yourself?
								</p>
							</div>

							<RadioGroup className="flex flex-col mb-0" name="expertise">
								<div className="optionLine">
									<RadioOption
										name="expertise"
										value="start"
										id="start"
										label="Just starting"
										handleChange={() =>
											handleRadioChange("start", "expertise", setForm)
										}
									/>

									<RadioOption
										name="expertise"
										value="basic"
										id="basic"
										label="Basic Knowledge"
										handleChange={() =>
											handleRadioChange("basic", "expertise", setForm)
										}
									/>
								</div>

								<div className="optionLine">
									<RadioOption
										name="expertise"
										value="intermediate"
										id="intermediate"
										label="Intermediate"
										handleChange={() =>
											handleRadioChange("intermediate", "expertise", setForm)
										}
									/>
									<RadioOption
										name="expertise"
										value="expert"
										id="expert"
										label="Expert"
										handleChange={() =>
											handleRadioChange("expert", "expertise", setForm)
										}
									/>
								</div>
							</RadioGroup>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "expertise",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 4 */}
						<div className="survey-question">
							<QuestionTitle title="What stocks do you typically invest in?" />

							<div className="flex flex-col space-y-2 text-lg">
								<div className="optionLine justify-start">
									<CheckboxOption
										name="stockTypes"
										id="growthStocks"
										label="Growth stocks"
										checked={form.stockTypes.includes("growthStocks")}
										handleChange={() =>
											handleCheckBoxChange(
												"growthStocks",
												"stockTypes",
												setForm,
											)
										}
									/>

									<CheckboxOption
										name="stockTypes"
										id="value"
										label="Value"
										checked={form.stockTypes.includes("value")}
										handleChange={() =>
											handleCheckBoxChange("value", "stockTypes", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-start">
									<CheckboxOption
										name="stockTypes"
										id="income"
										label="Income"
										checked={form.stockTypes.includes("income")}
										handleChange={() =>
											handleCheckBoxChange("income", "stockTypes", setForm)
										}
									/>
								</div>
							</div>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "stockTypes",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 5 */}
						<div className="survey-question">
							<QuestionTitle title="Besides stocks, do you use" />
							<div className="flex flex-col space-y-2 text-lg">
								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="otherInvestments"
										id="options"
										label="Options"
										checked={form.otherInvestments.includes("options")}
										handleChange={() =>
											handleCheckBoxChange(
												"options",
												"otherInvestments",
												setForm,
											)
										}
									/>
									<CheckboxOption
										name="otherInvestments"
										id="futures"
										label="Futures"
										checked={form.otherInvestments.includes("futures")}
										handleChange={() =>
											handleCheckBoxChange(
												"futures",
												"otherInvestments",
												setForm,
											)
										}
									/>
								</div>
								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="otherInvestments"
										id="etf"
										label="ETFs"
										checked={form.otherInvestments.includes("etf")}
										handleChange={() =>
											handleCheckBoxChange("etf", "otherInvestments", setForm)
										}
									/>
									<CheckboxOption
										name="otherInvestments"
										id="funds"
										label="Mutual Funds"
										checked={form.otherInvestments.includes("funds")}
										handleChange={() =>
											handleCheckBoxChange("funds", "otherInvestments", setForm)
										}
									/>
								</div>
							</div>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "otherInvestments",
									)?.message || ""
								}
							/>
						</div>
					</div>

					<div className="right-side w-full max-w-[600px]">
						{/* Question 6 */}
						<div className="survey-question">
							<QuestionTitle title="Why do you invest?" />

							<div className="flex flex-col space-y-2 text-lg">
								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="goal"
										id="money"
										label="To make some quick money"
										checked={form.goal.includes("money")}
										handleChange={() =>
											handleCheckBoxChange("money", "goal", setForm)
										}
									/>

									<CheckboxOption
										name="goal"
										id="expense"
										label="Meet an upcoming expense"
										checked={form.goal.includes("expense")}
										handleChange={() =>
											handleCheckBoxChange("expense", "goal", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="goal"
										id="wealth"
										label="To build long term wealth"
										checked={form.goal.includes("wealth")}
										handleChange={() =>
											handleCheckBoxChange("wealth", "goal", setForm)
										}
									/>

									<CheckboxOption
										name="goal"
										id="retirement"
										label="Retirement Planning"
										checked={form.goal.includes("retirement")}
										handleChange={() =>
											handleCheckBoxChange("retirement", "goal", setForm)
										}
									/>
								</div>
							</div>

							<ErrorMessage
								message={
									validationErrors?.issues.find((e) => e.path[0] === "goal")
										?.message || ""
								}
							/>
						</div>

						{/* Question 7 */}
						<div className="survey-question">
							<QuestionTitle title="What is the size of your portfolio?" />

							<RadioGroup className="flex flex-col mb-0" name="portfolioSize">
								<div className="optionLine">
									<RadioOption
										name="portfolioSize"
										value="less50k"
										id="less50k"
										label="Less than $50,000"
										handleChange={() =>
											handleRadioChange("less50k", "portfolioSize", setForm)
										}
									/>

									<RadioOption
										name="portfolioSize"
										value="50-100k"
										id="50-100k"
										label="Between $50,000 - $100,000"
										handleChange={() =>
											handleRadioChange("50-100k", "portfolioSize", setForm)
										}
									/>
								</div>

								<div className="optionLine">
									<RadioOption
										name="portfolioSize"
										value="more100k"
										id="more100k"
										label="More than $100,000"
										handleChange={() =>
											handleRadioChange("more100k", "portfolioSize", setForm)
										}
									/>

									<RadioOption
										name="portfolioSize"
										value="over1m"
										id="over1m"
										label="Over $1,000,000"
										handleChange={() =>
											handleRadioChange("over1m", "portfolioSize", setForm)
										}
									/>
								</div>
							</RadioGroup>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "portfolioSize",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 8 */}
						<div className="survey-question">
							<QuestionTitle title="How do you see yourself?" />
							<div className="flex flex-col space-y-2 text-lg">
								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="investorType"
										id="tradeStocks"
										label="I like to trade stocks"
										checked={form.investorType.includes("tradeStocks")}
										handleChange={() =>
											handleCheckBoxChange(
												"tradeStocks",
												"investorType",
												setForm,
											)
										}
									/>
									<CheckboxOption
										name="investorType"
										id="generallyInvest"
										label="I'll trade but generally invest"
										checked={form.investorType.includes("generallyInvest")}
										handleChange={() =>
											handleCheckBoxChange(
												"generallyInvest",
												"investorType",
												setForm,
											)
										}
									/>
								</div>
								<div className="optionLine justify-startflex-startgrow">
									<CheckboxOption
										name="investorType"
										id="investor"
										label="I'm an investor"
										checked={form.investorType.includes("investor")}
										handleChange={() =>
											handleCheckBoxChange("investor", "investorType", setForm)
										}
									/>
									<CheckboxOption
										name="investorType"
										id="hedge"
										label="Trade/Hedge with Options"
										checked={form.investorType.includes("options")}
										handleChange={() =>
											handleCheckBoxChange("options", "investorType", setForm)
										}
									/>
								</div>
							</div>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "investorType",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 9 */}
						<div className="survey-question">
							<QuestionTitle title="How long have you been investing?" />

							<RadioGroup
								className="flex flex-col mb-0"
								name="investmentDuration"
							>
								<div className="optionLine">
									<RadioOption
										name="investmentDuration"
										value="less12months"
										data-testid="less12months"
										id="less12months"
										label="Less than 12 months"
										handleChange={() =>
											handleRadioChange(
												"less12months",
												"investmentDuration",
												setForm,
											)
										}
									/>
									<RadioOption
										name="investmentDuration"
										value="1-5Years"
										id="1-5Years"
										label="1-5 years"
										handleChange={() =>
											handleRadioChange(
												"1-5Years",
												"investmentDuration",
												setForm,
											)
										}
									/>
								</div>

								<div className="optionLine">
									<RadioOption
										name="investmentDuration"
										value="5-10Years"
										id="5-10Years"
										label="5-10 Years"
										handleChange={() =>
											handleRadioChange(
												"5-10Years",
												"investmentDuration",
												setForm,
											)
										}
									/>
									<RadioOption
										name="investmentDuration"
										value="over10Years"
										id="over10Years"
										label="Over 10 Years"
										handleChange={() =>
											handleRadioChange(
												"over10Years",
												"investmentDuration",
												setForm,
											)
										}
									/>
								</div>
							</RadioGroup>

							<ErrorMessage
								message={
									validationErrors?.issues.find(
										(e) => e.path[0] === "investmentDuration",
									)?.message || ""
								}
							/>
						</div>

						{/* Question 10 */}
						<div className="survey-question mt-2">
							<QuestionTitle title="What other tools do you use?" />

							<div className="flex flex-col space-y-2 text-lg mb-2">
								<div className="optionLine justify-start">
									<CheckboxOption
										name="tools"
										id="bankBroker"
										label="Bank/Broker Platform"
										checked={form.tools.includes("bankBroker")}
										handleChange={() =>
											handleCheckBoxChange("bankBroker", "tools", setForm)
										}
									/>

									<CheckboxOption
										name="tools"
										id="stockTwits"
										label="StockTwits"
										checked={form.tools.includes("stockTwits")}
										handleChange={() =>
											handleCheckBoxChange("stockTwits", "tools", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-start">
									<CheckboxOption
										name="tools"
										id="benzinga"
										label="Benzinga"
										checked={form.tools.includes("benzinga")}
										handleChange={() =>
											handleCheckBoxChange("benzinga", "tools", setForm)
										}
									/>

									<CheckboxOption
										name="tools"
										id="tradingView"
										label="TradingView"
										checked={form.tools.includes("tradingView")}
										handleChange={() =>
											handleCheckBoxChange("tradingView", "tools", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-start">
									<CheckboxOption
										name="tools"
										id="ibd"
										label="IBD/Market Surge"
										checked={form.tools.includes("ibd")}
										handleChange={() =>
											handleCheckBoxChange("ibd", "tools", setForm)
										}
									/>

									<CheckboxOption
										name="tools"
										id="eTorro"
										label="Robin Hood/WeBull/ETorro"
										checked={form.tools.includes("eTorro")}
										handleChange={() =>
											handleCheckBoxChange("eTorro", "tools", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-start">
									<CheckboxOption
										name="tools"
										id="finvest"
										label="Finvest"
										checked={form.tools.includes("finvest")}
										handleChange={() =>
											handleCheckBoxChange("finvest", "tools", setForm)
										}
									/>

									<CheckboxOption
										name="tools"
										id="web"
										label="Social Media/Web"
										checked={form.tools.includes("web")}
										handleChange={() =>
											handleCheckBoxChange("web", "tools", setForm)
										}
									/>
								</div>

								<div className="optionLine justify-start">
									<CheckboxOption
										name="tools"
										id="vectorVest"
										label="VectorVest"
										checked={form.tools.includes("vectorVest")}
										handleChange={() =>
											handleCheckBoxChange("vectorVest", "tools", setForm)
										}
									/>

									<CheckboxOption
										name="tools"
										id="other"
										label="Other"
										checked={form.tools.includes("other")}
										handleChange={() =>
											handleCheckBoxChange("other", "tools", setForm)
										}
									/>
								</div>
							</div>
						</div>

						<ErrorMessage
							message={
								validationErrors?.issues.find((e) => e.path[0] === "tools")
									?.message || ""
							}
						/>
					</div>
				</div>

				{/* Terms checkbox */}
				<div className="survey-question">
					<div className="flex items-center space-x-2 w-full lg:w-1/2">
						<Checkbox
							id="terms"
							name="terms"
							checked={form.terms === "on"}
							onCheckedChange={(v) =>
								setForm({ ...form, terms: v ? "on" : "" })
							}
						/>
						<label
							htmlFor="terms"
							className="font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
						>
							By clicking here, I have read and accepted the{" "}
							<a
								href="https://epsmomentum.com/privacy-policy"
								className="text-gold text-bold cursor"
								target="_blank"
								rel="noreferrer"
							>
								privacy policy
							</a>
							,{" "}
							<a
								href="https://epsmomentum.com/terms-of-use"
								className="text-gold text-bold cursor"
								target="_blank"
								rel="noreferrer"
							>
								terms of use
							</a>{" "}
							and{" "}
							<a
								href="https://epsmomentum.com/terms-conditions"
								className="text-gold text-bold cursor"
								target="_blank"
								rel="noreferrer"
							>
								terms and conditions
							</a>{" "}
							documents.
						</label>
					</div>

					<ErrorMessage
						message={
							validationErrors?.issues.find((e) => e.path[0] === "terms")
								?.message || ""
						}
					/>
				</div>

				<div className="flex flex-col items-center justify-center">
					<button
						disabled={isLoadingUpdate}
						type="submit"
						data-testid="survey-submit"
						data-tracking="survey-submit"
						className="max-w-[150px] text-lg submit-btn px-6 py-2 bg-gold hover:bg-gray text-black hover:text-white font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 cursor-pointer"
					>
						Next
					</button>

					<ErrorMessage
						message={updateError || ""}
						className="flex justify-center mt-2"
						messageStyle="text-center"
					/>
				</div>

				<p className="text-lg text-center font-bold">
					Thank you and enjoy using EPSMomentum!
				</p>
			</div>

			{isLoadingUpdate && <Spinner />}
		</form>
	);
};
